import { cx } from "../ui"

/**
 * A number that does not need a plot.
 *
 * `tone` maps to the same two state colours the chart uses, so an "Open" tile
 * and the open line read as the same thing. The icon and the label carry the
 * meaning; the colour only reinforces it.
 */

const TONES = {
	neutral: { value: "text-ink-900", accent: "bg-ink-200" },
	open: { value: "text-state-open-ink", accent: "bg-state-open" },
	closed: { value: "text-state-closed-ink", accent: "bg-state-closed" },
	brand: { value: "text-brand-800", accent: "bg-brand-500" }
}

export function StatTile({ label, value, hint, tone = "neutral", icon: Icon, className }) {
	const palette = TONES[tone] || TONES.neutral
	return (
		<div
			className={cx(
				"relative overflow-hidden rounded-card border border-ink-200/80 bg-white px-4 py-3 shadow-card",
				className
			)}
		>
			<span
				className={cx("absolute inset-y-0 left-0 w-1", palette.accent)}
				aria-hidden
			/>
			<div className="flex items-center gap-1.5">
				{Icon && <Icon size={13} className="text-ink-400" aria-hidden />}
				<p className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
					{label}
				</p>
			</div>
			<p className={cx("mt-1 text-2xl font-semibold leading-none tabular", palette.value)}>
				{value}
			</p>
			{hint && <p className="mt-1.5 text-2xs text-ink-500">{hint}</p>}
		</div>
	)
}

/** Horizontal share bar used for the per-tag breakdown. */
export function SplitBar({ open, closed, className }) {
	const total = Math.max(1, open + closed)
	return (
		<div
			className={cx("flex h-1.5 w-full overflow-hidden rounded-full bg-ink-100", className)}
			role="img"
			aria-label={`${open} open, ${closed} resolved`}
		>
			{closed > 0 && (
				<span
					className="bg-state-closed"
					style={{ width: `${(closed / total) * 100}%` }}
				/>
			)}
			{/* A 2px surface gap keeps the two fills from reading as one bar. */}
			{closed > 0 && open > 0 && <span className="w-0.5 bg-white" aria-hidden />}
			{open > 0 && (
				<span className="bg-state-open" style={{ width: `${(open / total) * 100}%` }} />
			)}
		</div>
	)
}
